import React, { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { X } from 'lucide-react';
import { transactionApi } from '../../../api/transactionApi';
import { Transaction, TransactionFilterParams } from '../../../api/types';
import LedgerTable from '../../ledger/cash/CashTable';
import TransactionDrawer from '../../ledger/cash/TransactionDrawer';
import formatCurrency from '../../../utils/formatCurrency';

interface WeeklyDrilldownModalProps {
    isOpen: boolean;
    onClose: () => void;
    title: string;
    startDate: string;
    endDate: string;
    startAmount: number;
    initialFilters?: Partial<TransactionFilterParams>;
    businessId?: string;
    tutorialTransactions?: Transaction[];
}

const WeeklyDrilldownModal: React.FC<WeeklyDrilldownModalProps> = ({
    isOpen,
    onClose,
    title,
    startDate,
    endDate,
    startAmount,
    initialFilters = {},
    businessId,
    tutorialTransactions
}) => {
    const [selectedTransactionId, setSelectedTransactionId] = useState<string | null>(null);
    const [selectedTutorialTransaction, setSelectedTutorialTransaction] = useState<Transaction | null>(null);
    const [filters, setFilters] = useState<TransactionFilterParams>({
        page: 1,
        page_size: 25,
        start_date: startDate,
        end_date: endDate,
        sort_by: 'date',
        sort_order: 'desc',
        business_id: businessId,
        ...initialFilters
    });

    // Reset filters when date range or initial filters change
    useEffect(() => {
        if (isOpen) {
            setFilters({
                page: 1,
                page_size: 25,
                start_date: startDate,
                end_date: endDate,
                sort_by: 'date',
                sort_order: 'desc',
                business_id: businessId,
                ...initialFilters
            });
        }
    }, [isOpen, startDate, endDate, JSON.stringify(initialFilters), businessId]);

    const { data, isLoading } = useQuery({
        queryKey: ['transactions', filters, businessId],
        queryFn: () => transactionApi.getTransactions(filters),
        enabled: isOpen && !tutorialTransactions,
    });
    const tableTransactions = tutorialTransactions || data?.items || [];
    const tableTotal = tutorialTransactions ? tutorialTransactions.length : data?.total || 0;

    const hasActiveFilters = Boolean(
        filters.start_date
        || filters.end_date
        || filters.search
        || filters.vendor_name
        || filters.direction
        || filters.operating_flag !== undefined
    );

    const handleRowClick = (id: string) => {
        if (tutorialTransactions) {
            setSelectedTutorialTransaction(tutorialTransactions.find((transaction) => transaction._id === id) || null);
        } else {
            setSelectedTutorialTransaction(null);
        }
        setSelectedTransactionId(id);
    };

    const handlePageChange = (newPage: number) => {
        setFilters(prev => ({ ...prev, page: newPage }));
    };

    const handleSort = (column: string) => {
        setFilters(prev => {
            const isAsc = prev.sort_by === column && prev.sort_order === 'asc';
            return {
                ...prev,
                sort_by: column,
                sort_order: isAsc ? 'desc' : 'asc',
                page: 1
            };
        });
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-x-0 bottom-0 top-[50px] z-50 flex flex-col overflow-hidden sm:inset-0 sm:items-center sm:justify-center">
            {/* Overlay */}
            <div className="fixed inset-0 bg-gray-600 bg-opacity-75 transition-opacity" onClick={onClose} />

            {/* Modal Container */}
            <div
                className="relative flex h-full w-full transform flex-col overflow-hidden bg-white shadow-xl transition-all sm:my-8 sm:h-[60vh] sm:max-w-6xl sm:rounded-lg"
                onClick={e => e.stopPropagation()}
            >
                {/* Header: Sticky at the top */}
                <div className="flex flex-shrink-0 items-start justify-between border-b border-gray-200 bg-white px-4 py-3 sm:items-center sm:px-6 sm:py-4">
                    <div className="min-w-0 flex-1">
                        <h3 className="text-base sm:text-lg font-bold leading-6 text-gray-900 truncate">
                            {title}
                        </h3>
                        <p className="mt-0.5 text-xs sm:text-sm text-gray-500">
                            Total: <span className="font-semibold text-gray-900">{formatCurrency(startAmount)}</span>
                        </p>
                    </div>
                    <button
                        onClick={onClose}
                        className="ml-3 flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-gray-50 text-gray-500 transition-colors hover:bg-gray-100 focus:outline-none sm:h-10 sm:w-10"
                        aria-label="Close transactions modal"
                    >
                        <X size={20} />
                    </button>
                </div>

                {/* Table Area: Scrollable */}
                <div className="flex min-h-0 flex-1 flex-col overflow-hidden bg-white">
                    {/* Note: Ensure LedgerTable is responsive. 
                        If the table is too wide, add overflow-x-auto here.
                    */}
                    <div className="flex min-h-0 flex-1 min-w-full flex-col">
                        <LedgerTable
                            transactions={tableTransactions}
                            isLoading={!tutorialTransactions && isLoading}
                            onRowClick={handleRowClick}
                            total={tableTotal}
                            page={filters.page || 1}
                            pageSize={filters.page_size || 25}
                            onPageChange={handlePageChange}
                            hasActiveFilters={hasActiveFilters}
                            sortBy={filters.sort_by}
                            sortOrder={filters.sort_order}
                            onSort={handleSort}
                        />
                    </div>
                </div>

                {/* Safe area spacer for mobile home bar */}
                <div className="h-[env(safe-area-inset-bottom)] flex-shrink-0 bg-white sm:hidden" />
            </div>

            <TransactionDrawer
                transactionId={selectedTransactionId}
                open={!!selectedTransactionId}
                onClose={() => {
                    setSelectedTransactionId(null);
                    setSelectedTutorialTransaction(null);
                }}
                businessId={businessId}
                preloadedTransaction={selectedTutorialTransaction}
            />
        </div>
    );
};

export default WeeklyDrilldownModal;
